
import React from "react";
import { AlertTriangle, ShieldCheck, CalendarClock, CheckSquare } from "lucide-react";

export default function StudentReportView({
  reportData,
  fromDate,
  toDate,
  hideHeader
}) {
  const students = reportData?.students || [];
  const cohortName = reportData?.cohort_name || "Cohort";
  const batchName = reportData?.batch_name || "Batch";
  const subjects = reportData?.subjects || {};

  // Extract the totals from backend (or fallback to 0)
  const classesScheduled = reportData?.classes_scheduled || "-";
  
  const formatTeacherName = (name) => {
    if (!name) return "-";

    let cleaned = name.trim().replace(/\s+/g, " ");

    if (!/^(MR|MRS|MS|DR)\b/i.test(cleaned)) {
      return cleaned
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    const parts = cleaned.split(" ");
    let rawTitle = parts[0].toUpperCase();
    let titleMap = { MR: "Mr.", MRS: "Mrs.", MS: "Ms.", DR: "Dr." };
    let title = titleMap[rawTitle] || "Mr.";
    let nameParts = parts.slice(1);

    if (nameParts.length === 0) return title;

    let formattedName = nameParts
      .map((part) => {
        if (part.length === 1) return part.toUpperCase();
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      })
      .join(" ");

    return `${title} ${formattedName}`;
  };

  // 🔥 Flatten columns and include scheduled (if available)
  const columns = [];
  Object.entries(subjects).forEach(([subject, teachers]) => {
    teachers.forEach((t) => {
      columns.push({
        subject,
        teacher: t.teacher_name,
        conducted: t.conducted,
        scheduled: t.scheduled || "-" // Will show '-' until backend provides this per subject
      });
    });
  });

  // Calculate total conducted based on the columns
  const classesConducted = reportData?.classes_taken || columns.reduce((acc, col) => acc + col.conducted, 0);

  const LOW_ATTENDANCE_THRESHOLD = 75.0;

  return (
    <div className="space-y-6">

      {!hideHeader && (
        <div className="flex flex-col gap-4">
          {/* Main Title Banner */}
          <div className="flex justify-between items-center bg-slate-50 border border-slate-200 rounded-lg p-4 shadow-sm text-slate-700">
            <div className="text-sm">From: <b className="text-slate-900">{fromDate}</b></div>
            <div className="text-lg font-extrabold text-center text-blue-900">
              {cohortName} - {batchName} - ATTENDANCE REPORT
            </div>
            <div className="text-sm">To: <b className="text-slate-900">{toDate}</b></div>
          </div>

          {/* STATS SUMMARY BAR */}
          <div className="flex justify-center items-center bg-white border border-slate-200 rounded-lg p-4 shadow-sm divide-x divide-slate-200">
            <div className="flex flex-col items-center px-12">
              <div className="flex items-center text-slate-500 mb-1">
                <CalendarClock size={16} className="mr-2" />
                <span className="text-xs font-bold uppercase tracking-wider">Total Scheduled</span>
              </div>
              <span className="text-2xl font-black text-blue-900">{classesScheduled}</span>
            </div>
            
            <div className="flex flex-col items-center px-12">
              <div className="flex items-center text-slate-500 mb-1">
                <CheckSquare size={16} className="mr-2" />
                <span className="text-xs font-bold uppercase tracking-wider">Total Conducted</span>
              </div>
              <span className="text-2xl font-black text-green-600">{classesConducted}</span>
            </div>
          </div>
        </div>
      )}

      {students.length === 0 ? (
        <p className="text-center text-gray-400 py-12 border rounded-lg bg-white shadow-sm">
          No student attendance data available for this period.
        </p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-slate-50">
              {/* 🔥 HEADER ROW 1 (SUBJECTS & SCHEDULED COUNT) */}
              <tr>
                <th rowSpan="2" className="px-4 py-3 text-center border-r border-gray-200 font-bold text-gray-700">Sl No</th>
                <th rowSpan="2" className="px-4 py-3 text-left border-r border-gray-200 font-bold text-gray-700 min-w-[180px]">Student Name</th>

                {columns.map((col, idx) => (
                  <th key={`subj-${idx}`} className="px-3 py-2 text-center border-r border-gray-200 text-sky-700 font-extrabold leading-tight min-w-[140px]">
                    <div>{col.subject} ({col.scheduled})</div>
                  </th>
                ))}

                <th rowSpan="2" className="px-4 py-3 text-center border-r border-gray-200 font-bold text-gray-700">Attended</th>
                <th rowSpan="2" className="px-4 py-3 text-center border-r border-gray-200 font-bold text-gray-700">%</th>
              </tr>

              {/* 🔥 HEADER ROW 2 (TEACHERS & CONDUCTED COUNT) */}
              <tr>
                {columns.map((col, idx) => (
                  <th key={`teacher-${idx}`} className="px-3 py-2 text-center border-r border-gray-200 text-xs text-slate-700 font-bold bg-white leading-tight">
                    <div>{formatTeacherName(col.teacher)} ({col.conducted})</div>                   
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {students.map((s, idx) => {
                let totalAttended = 0;
                let totalConducted = 0;

                return (
                  <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                    <td className="px-4 py-2.5 text-center border-r border-gray-100 text-slate-500 font-semibold">{idx + 1}</td>
                    <td className="px-4 py-2.5 text-left border-r border-gray-100 font-bold text-blue-900">{s.name || s.student_name}</td>

                    {/* DYNAMIC SUBJECT CELLS */}
                    {columns.map((col, i) => {
                      const val = s.subjects?.[col.subject]?.[col.teacher] ?? 0;
                      totalAttended += val;
                      totalConducted += col.conducted;

                      return (
                        <td key={`cell-${i}`} className="px-3 py-2.5 text-center border-r border-gray-100">
                          {val > 0 ? (
                            <span className="text-green-600 font-bold">{val}</span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      );
                    })}

                    {/* TOTAL + % */}
                    <td className="px-4 py-2.5 text-center border-r border-gray-100 font-bold text-slate-700">
                      {totalAttended}
                    </td>

                    <td className="px-4 py-2.5 text-center border-r border-gray-100 font-extrabold text-slate-800">
                      {totalConducted > 0
                        ? Math.round((totalAttended / totalConducted) * 100)
                        : 0}%
                    </td>                   
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}